import { Component, OnDestroy, OnInit } from '@angular/core';
import { UpdateAppConfig } from '@app/features/app-content/states/app-configuration.actions';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { DropdownOption } from '@app/ui/components/dropdown/dropdown.model';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription, combineLatest, filter } from 'rxjs';

// extends dropdown option with cyrillic support flag
interface FontOption extends DropdownOption {
  cyrillic?: boolean;
}

@Component({
  selector: 'rk-font-family-selector',
  templateUrl: './font-family-selector.component.html',
  styleUrl: './font-family-selector.component.scss',
  standalone: false,
})
export class FontFamilySelectorComponent implements OnInit, OnDestroy {
  private readonly allFonts: FontOption[] = [
    { value: 'Quicksand', label: 'Quicksand' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'DancingScript', label: 'Dancing Script' },
    { value: 'Solway', label: 'Solway' },
    { value: 'Oswald', label: 'Oswald', cyrillic: true },
    { value: 'Raleway', label: 'Raleway', cyrillic: true },
    { value: 'Roboto', label: 'Roboto', cyrillic: true },
    { value: 'RobotoMono', label: 'Roboto Mono', cyrillic: true },
    { value: 'PlayfairDisplay', label: 'Playfair Display', cyrillic: true },
    { value: 'JosefinSans', label: 'Josefin Sans' },
    { value: 'EBGaramond', label: 'EB Garamond', cyrillic: true },
    { value: 'Comfortaa', label: 'Comfortaa', cyrillic: true },
    { value: 'Fedroka', label: 'Fedroka' },
  ];

  fontFamilyOptions: DropdownOption[] = [...this.allFonts];
  selectedFont: DropdownOption;

  @Select(AppConfigurationState.fontFamily)
  fontFamily$: Observable<string>;

  @Select(AppConfigurationState.currentAppConfigLanguage)
  currentAppConfigLanguage$: Observable<string>;

  private readonly subscription = new Subscription();

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.subscription.add(
      combineLatest([this.fontFamily$, this.currentAppConfigLanguage$])
        .pipe(filter(([font, lang]) => !!font && !!lang))
        .subscribe(([font, lang]) => {
          const isRussian = lang === 'ru';
          this.fontFamilyOptions = isRussian
            ? this.allFonts.filter(f => f.cyrillic)
            : [...this.allFonts];
          this.selectedFont =
            this.fontFamilyOptions.find(o => o.value === font) || this.fontFamilyOptions[0];

          // auto-switch font if current one is not available for this language
          if (this.selectedFont.value !== font) {
            this.onFontSelectionChange(this.selectedFont.value);
          }
        }),
    );
  }

  onFontSelectionChange(value: string) {
    this.store.dispatch(new UpdateAppConfig(['onglets', '0', 'fontFamily'], value));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
